import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { initiateRegistrationPayment, checkPaymentStatus, getRegistrationFee } from '../../services/payments';
import { 
  Loader2, CheckCircle2, XCircle, Phone, 
  Smartphone, ShieldCheck, RefreshCcw, Lock, 
  Zap, ChevronRight 
} from 'lucide-react';

const RegistrationPayment = () => {
  const { memberId } = useParams();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [status, setStatus] = useState('pending'); 
  const [fee, setFee] = useState(null);
  const [phone, setPhone] = useState('');

  useEffect(() => {
    const fetchFee = async () => {
      try {
        const response = await getRegistrationFee();
        setFee(response.fee);
      } catch (err) {
        setError('Connection Error: Sync failed.');
      }
    };
    fetchFee();
  }, []);

  const handlePay = async () => {
    if (phone.length < 9) return;
    setLoading(true);
    setError('');
    try {
      const formattedPhone = phone.startsWith('0') ? '254' + phone.slice(1) : '254' + phone;
      await initiateRegistrationPayment({ member_id: memberId, phone: formattedPhone });
      setStatus('stk_sent');
    } catch (err) {
      setError(err.response?.data?.error || 'M-Pesa Gateway Timeout');
      setLoading(false);
    }
  };

  useEffect(() => {
    let interval;
    if (status === 'stk_sent') {
      interval = setInterval(async () => {
        try {
          const res = await checkPaymentStatus({ member_id: memberId });
          if (res.status === 'paid') {
            clearInterval(interval);
            setStatus('paid');
            setTimeout(() => navigate('/login'), 3000);
          } else if (res.status === 'failed') {
            clearInterval(interval);
            setStatus('failed');
            setLoading(false);
          }
        } catch (err) { console.error(err); }
      }, 3000);
    }
    return () => clearInterval(interval);
  }, [status, memberId, navigate]);

  return (
    /* GRADIENT BACKDROP: Matching Registration/Login Page */
    <div 
      className="min-h-screen flex items-center justify-center p-6 font-sans"
      style={{ background: 'linear-gradient(135deg, #162f68 0%, #015bbe 100%)' }}
    >
      {/* Subtle brand watermark in background */}
      <div className="absolute top-10 left-10 opacity-10 select-none hidden lg:block">
        <h1 className="text-9xl font-black text-white italic">NYM</h1>
      </div>

      <div className="w-full max-w-[440px] relative animate-in fade-in zoom-in-95 duration-700">
        
        {/* GLASSMORPHISM CONTAINER */}
        <div className="bg-white/95 backdrop-blur-xl rounded-[2.5rem] shadow-[0_40px_100px_-20px_rgba(0,0,0,0.4)] overflow-hidden">
          
          {/* HEADER SECTION */}
          <div className="p-10 pb-6 text-center">
            <div className="w-16 h-16 bg-[#162f68] rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg shadow-[#162f68]/20">
              <Zap size={28} className="text-[#eda006]" fill="currentColor" />
            </div>
            <h2 className="text-2xl font-black text-[#162f68] tracking-tight">Complete Activation</h2>
            <p className="text-slate-500 text-xs font-medium mt-2 uppercase tracking-widest">Quakermen Programme</p>
          </div>

          <div className="px-10 pb-10 space-y-8">
            
            {/* AMOUNT BLOCK: Matches Login Input Style */}
            <div className="bg-slate-50 rounded-2xl p-6 border border-slate-100 text-center">
              <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Total Payable</p>
              <h1 className="text-5xl font-black text-[#015bbe] tracking-tighter">
                 <span className="text-base font-bold opacity-30 mr-1">KSh</span>
                 {fee?.toLocaleString() || '---'}
              </h1>
            </div>

            {status === 'pending' ? (
              <div className="space-y-6">
                <div className="space-y-3">
                  <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest ml-1">M-Pesa Number</label>
                  <div className="relative group">
                    <div className="absolute left-6 top-1/2 -translate-y-1/2 pr-4 border-r border-slate-200">
                       <span className="font-black text-[#162f68]">+254</span>
                    </div>
                    <input
                      type="tel"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value.replace(/\D/g, '').slice(0, 10))}
                      placeholder="7XX XXX XXX"
                      className="w-full pl-24 pr-6 py-5 bg-white border-2 border-slate-100 rounded-2xl font-black text-[#162f68] text-lg focus:border-[#eda006] outline-none transition-all"
                    />
                  </div>
                </div>

                {/* THE RED BUTTON: Core Branded Action */}
                <button
                  onClick={handlePay}
                  disabled={loading || phone.length < 9}
                  className="w-full py-5 bg-[#ff1901] text-white rounded-2xl font-black text-xs uppercase tracking-[0.3em] shadow-xl shadow-rose-200 hover:brightness-110 active:scale-95 transition-all flex items-center justify-center gap-3"
                >
                  {loading ? <Loader2 className="animate-spin" size={20} /> : (
                    <>
                      <span>Submit Payment</span>
                      <ChevronRight size={18} strokeWidth={3} />
                    </>
                  )}
                </button>
              </div>
            ) : status === 'stk_sent' ? (
              <div className="text-center space-y-8 py-4 animate-in zoom-in-95">
                <div className="relative inline-flex items-center justify-center">
                  <div className="w-20 h-20 bg-rose-50 text-[#ff1901] rounded-full flex items-center justify-center">
                    <Smartphone size={32} className="animate-bounce" />
                  </div>
                  <div className="absolute inset-0 border-2 border-[#ff1901] rounded-full animate-ping opacity-20" />
                </div>
                <div className="space-y-2">
                  <h3 className="text-xl font-black text-[#162f68]">Awaiting PIN</h3>
                  <p className="text-xs text-slate-500 font-medium px-6 leading-relaxed">
                    Check your phone for the M-Pesa prompt. Enter your PIN to finalize your registration.
                  </p>
                </div>
                <button onClick={() => setStatus('pending')} className="text-[10px] font-black text-slate-300 uppercase tracking-widest hover:text-[#ff1901] transition-colors flex items-center gap-2 mx-auto">
                  <RefreshCcw size={14} /> Back to Edit
                </button>
              </div>
            ) : (
              <div className="text-center py-6 animate-in zoom-in-90 space-y-4">
                  <div className="w-16 h-16 bg-emerald-50 text-emerald-500 rounded-full flex items-center justify-center mx-auto shadow-lg shadow-emerald-100">
                    <CheckCircle2 size={32} />
                  </div>
                  <h2 className="text-2xl font-black text-[#162f68]">Success!</h2>
                  <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Redirecting to login...</p>
              </div>
            )}
          </div>

          {/* SHARED BRAND FOOTER */}
          <div className="bg-slate-50/50 p-6 flex items-center justify-between px-10 border-t border-slate-100">
             <img src="https://upload.wikimedia.org/wikipedia/commons/thumb/1/15/M-PESA_LOGO-01.svg/1200px-M-PESA_LOGO-01.svg.png" className="h-4 opacity-40 grayscale" alt="mpesa" />
             <div className="flex items-center gap-3 opacity-30">
                <ShieldCheck size={14} className="text-[#162f68]" />
                <Lock size={14} className="text-[#162f68]" />
             </div>
          </div>
        </div>

        {/* EXTERNAL TRUST TEXT */}
        <p className="text-center mt-8 text-[9px] font-black text-white/40 uppercase tracking-[0.4em]">
           Secured by NYM Infrastructure
        </p>

      </div>

      {/* ERROR TOAST: Overlays the gradient nicely */}
      {error && (
        <div className="fixed bottom-10 bg-[#ff1901] text-white px-6 py-4 rounded-2xl shadow-2xl flex items-center gap-3 animate-in slide-in-from-bottom-5">
           <XCircle size={18} />
           <p className="text-[10px] font-black uppercase tracking-widest font-sans">{error}</p>
        </div>
      )}
    </div>
  );
};

export default RegistrationPayment;